// Typed client for ASSET BOOKINGS — equipment reserved for a time, in the
// venue's calendar (docs/domains/ASSET_BOOKING.md). On the `http` backend
// every write is a narrow command on `/asset-bookings`; on the memory backend
// (tests, no API) the list is a blob under `asset_bookings_v1`, validated with
// the same engine the server runs, so the screens behave the same either way.
import {
  assetBookingCandidate,
  calcAssetBookingPrice,
  validateAssetBooking,
  type AssetBookingRefusalCode,
} from '../../engine';
import { Storage } from '../storage';
import { apiFetch } from './http';
import { AssetCommandError, type Asset } from './assets';

const KEY = 'asset_bookings_v1';

/** An asset booking as the API returns it — the court booking by its `book_…` id. */
export interface AssetBooking {
  id: string;
  assetId: string;
  /** The court booking it rides with, or null when it stands alone. */
  bookingId: string | null;
  date: string;
  startTime: string;
  endTime: string;
  durationMin: number;
  priceNok: number;
  paymentStatus: 'free' | 'pending' | 'paid' | string;
  source: string;
  title: string | null;
  notes: string;
  bookedBy: { name?: string; phone?: string; email?: string } | null;
  createdAt: number;
  /**
   * On the MEMBER surface only (docs/domains/ASSET_BOOKING.md, slice 2): whether
   * this is the viewer's own row. The projection answers it the way it answers
   * for court bookings; the club surface never sends it.
   */
  mine?: boolean;
}

/** What a client sends. Price and status are optional staff statements (#634). */
export interface AssetBookingIntent {
  assetId: string;
  date: string;
  startTime: string;
  durationMin?: number;
  endTime?: string;
  /** Attach to a court booking by its `book_…` id. */
  bookingId?: string | null;
  title?: string | null;
  notes?: string | null;
  bookedBy?: { name?: string; phone?: string; email?: string } | null;
  priceNok?: number;
  paymentStatus?: 'free' | 'pending';
  /** Relax the asset's gap rule and the opening hours — never the overlap. */
  adminOverride?: boolean;
}

export const assetBookingsAreServerAuthoritative = () => Storage.backend === 'http';

/** The codes the server (and the local validator) can answer with. */
export type AssetBookingErrorCode =
  | AssetBookingRefusalCode
  | 'asset_inactive'
  | 'asset_not_found'
  | 'asset_outside_booking'
  | 'booking_not_found'
  | 'payment_status_not_operator_controlled'
  | 'payment_status_contradicts_price'
  | 'not_found'
  | string;

async function refusalFrom(res: Response): Promise<AssetCommandError> {
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  const code = typeof body?.error === 'string' ? body.error : `http_${res.status}`;
  return new AssetCommandError(code, res.status, body);
}

async function readLocal(): Promise<AssetBooking[]> {
  const r = await Storage.get(KEY);
  return r?.value ? (JSON.parse(r.value) as AssetBooking[]) : [];
}

async function writeLocal(list: AssetBooking[]): Promise<void> {
  await Storage.set(KEY, JSON.stringify(list));
}

export async function fetchAssetBookings(): Promise<AssetBooking[]> {
  if (assetBookingsAreServerAuthoritative()) {
    const res = await apiFetch('/asset-bookings');
    if (!res.ok) return [];
    return (await res.json()) as AssetBooking[];
  }
  return readLocal();
}

/**
 * The memory backend's version of the server command: the same engine, the
 * same refusal codes, so a screen tested off-line sees what the server says.
 * `assets` and `cfg` are what the server would read from its own tables.
 */
export interface LocalAssetBookingContext {
  assets: Asset[];
  cfg: Record<string, unknown> | null;
}

function localCreate(
  intent: AssetBookingIntent,
  existing: AssetBooking[],
  ctx: LocalAssetBookingContext,
  id: string,
): AssetBooking {
  const asset = ctx.assets.find((a) => a.id === intent.assetId);
  if (!asset) throw new AssetCommandError('asset_not_found', 404);
  if (!asset.active) throw new AssetCommandError('asset_inactive', 409);
  const cand = assetBookingCandidate({ ...intent, id });
  const cfg = ctx.cfg ?? {};
  const rules = intent.adminOverride
    ? { bufferMin: 0 }
    : {
        bufferMin: asset.bufferMin ?? 0,
        ...(typeof cfg.openTime === 'string' && typeof cfg.closeTime === 'string'
          ? { openTime: cfg.openTime as string, closeTime: cfg.closeTime as string }
          : {}),
      };
  const v = validateAssetBooking(cand, existing, rules);
  if (!v.ok) {
    throw new AssetCommandError(v.code ?? 'invalid', 409, {
      conflictId: v.conflictId ?? null,
      detail: v.detail ?? null,
    });
  }
  const priceNok =
    typeof intent.priceNok === 'number'
      ? Math.trunc(intent.priceNok)
      : calcAssetBookingPrice(asset, cand.date, cand.startTime, cand.endTime, cfg).total;
  const derived = priceNok > 0 ? 'pending' : 'free';
  if (intent.paymentStatus && intent.paymentStatus !== derived) {
    throw new AssetCommandError('payment_status_contradicts_price', 409);
  }
  return {
    id,
    assetId: cand.assetId,
    bookingId: intent.bookingId ?? null,
    date: cand.date,
    startTime: cand.startTime,
    endTime: cand.endTime,
    durationMin: cand.durationMin,
    priceNok,
    paymentStatus: intent.paymentStatus ?? derived,
    source: 'admin',
    title: intent.title ?? null,
    notes: intent.notes ?? '',
    bookedBy: intent.bookedBy ?? null,
    createdAt: Date.now(),
  };
}

export async function createAssetBooking(
  intent: AssetBookingIntent,
  local: LocalAssetBookingContext | null = null,
): Promise<AssetBooking> {
  if (assetBookingsAreServerAuthoritative()) {
    const res = await apiFetch('/asset-bookings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(intent),
    });
    if (!res.ok) throw await refusalFrom(res);
    return (await res.json()) as AssetBooking;
  }
  const list = await readLocal();
  const created = localCreate(
    intent,
    list,
    local ?? { assets: [], cfg: null },
    `ab_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  );
  await writeLocal([...list, created]);
  return created;
}

export async function patchAssetBooking(
  id: string,
  patch: Partial<AssetBookingIntent>,
  local: LocalAssetBookingContext | null = null,
): Promise<AssetBooking> {
  if (assetBookingsAreServerAuthoritative()) {
    const res = await apiFetch('/asset-bookings/' + encodeURIComponent(id), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw await refusalFrom(res);
    return (await res.json()) as AssetBooking;
  }
  const list = await readLocal();
  const idx = list.findIndex((b) => b.id === id);
  if (idx < 0) throw new AssetCommandError('not_found', 404);
  const pre = list[idx];
  const merged: AssetBookingIntent = {
    assetId: patch.assetId ?? pre.assetId,
    date: patch.date ?? pre.date,
    startTime: patch.startTime ?? pre.startTime,
    ...(patch.endTime !== undefined || patch.durationMin !== undefined
      ? { endTime: patch.endTime, durationMin: patch.durationMin }
      : { endTime: pre.endTime }),
    bookingId: 'bookingId' in patch ? (patch.bookingId ?? null) : pre.bookingId,
    title: 'title' in patch ? (patch.title ?? null) : pre.title,
    notes: 'notes' in patch ? (patch.notes ?? '') : pre.notes,
    bookedBy: 'bookedBy' in patch ? (patch.bookedBy ?? null) : pre.bookedBy,
    ...(patch.priceNok !== undefined ? { priceNok: patch.priceNok } : {}),
    ...(patch.paymentStatus !== undefined ? { paymentStatus: patch.paymentStatus } : {}),
    adminOverride: patch.adminOverride,
  };
  const next = localCreate(merged, list, local ?? { assets: [], cfg: null }, id);
  const out = [...list];
  out[idx] = { ...next, createdAt: pre.createdAt };
  await writeLocal(out);
  return out[idx];
}

export async function deleteAssetBooking(id: string): Promise<void> {
  if (assetBookingsAreServerAuthoritative()) {
    const res = await apiFetch('/asset-bookings/' + encodeURIComponent(id), { method: 'DELETE' });
    if (!res.ok) throw await refusalFrom(res);
    return;
  }
  const list = await readLocal();
  await writeLocal(list.filter((b) => b.id !== id));
}

/**
 * The memory backend's stand-in for `ON DELETE CASCADE`: the court bookings
 * named were deleted, so their equipment goes too. A no-op on the API backend,
 * where the database already did it.
 */
export async function dropLocalAssetBookingsAttachedTo(
  bookingIds: readonly string[],
): Promise<void> {
  if (assetBookingsAreServerAuthoritative()) return;
  const ids = new Set(bookingIds.map(String));
  const list = await readLocal();
  const kept = list.filter((b) => !b.bookingId || !ids.has(String(b.bookingId)));
  if (kept.length !== list.length) await writeLocal(kept);
}

/**
 * The equipment attached to a court booking, by the booking's `book_…` id.
 * Shared by the grid, the edit modal and the delete confirmation.
 */
export function assetBookingsAttachedTo(
  list: readonly AssetBooking[],
  bookingId: string | null | undefined,
) {
  if (!bookingId) return [] as AssetBooking[];
  return list.filter((b) => b.bookingId === bookingId);
}
